import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Autocomplete,
  Chip,
  Typography,
  FormControlLabel,
  Switch,
  Grid,
  Card,
  CardMedia,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { adminService } from '../../services/adminService';

const ServiceModal = ({ open, onClose, onSuccess, service = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    images: [],
    userId: '',
    boutiqueId: '',
    categoryIds: [],
    isAvailable: true,
    isActive: true
  });
  const [users, setUsers] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [categorieServices, setCategorieServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingBoutiques, setLoadingBoutiques] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBoutique, setSelectedBoutique] = useState(null);
  const [error, setError] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    if (open) {
      loadUsers();
      loadBoutiques();
      loadCategorieServices();
    }
  }, [open]);

  useEffect(() => {
    if (service && users.length > 0 && boutiques.length > 0) {
      const user = users.find(u => u.id === service.userId);
      const boutique = boutiques.find(b => b.id === service.boutiqueId);
      
      setFormData({
        name: service.name || '',
        description: service.description || '',
        price: service.price ? parseFloat(service.price).toString() : '',
        images: service.images || [],
        userId: service.userId || '',
        boutiqueId: service.boutiqueId || '',
        categoryIds: service.categories 
          ? service.categories.map(c => c.categorieService?.id || c.id)
          : [],
        isAvailable: service.isAvailable !== undefined ? service.isAvailable : true,
        isActive: service.isActive !== undefined ? service.isActive : true
      });
      setSelectedUser(user || null);
      setSelectedBoutique(boutique || null);
      setImagePreviews(service.images || []);
      setImageFiles([]);
    } else if (!service) {
      setFormData({
        name: '',
        description: '',
        price: '',
        images: [],
        userId: '',
        boutiqueId: '',
        categoryIds: [],
        isAvailable: true,
        isActive: true
      });
      setSelectedUser(null);
      setSelectedBoutique(null);
      setImagePreviews([]);
      setImageFiles([]);
    }
    setError('');
  }, [service, open, users, boutiques]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      // Récupérer les utilisateurs avec les rôles VENDOR et USER
      const response = await adminService.getUsers({ roles: ['VENDOR', 'USER'] });
      setUsers(response.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadBoutiques = async () => {
    setLoadingBoutiques(true);
    try {
      const response = await adminService.getBoutiques();
      setBoutiques(response.boutiques || []);
    } catch (err) {
      console.error('Error loading boutiques:', err);
      setBoutiques([]);
    } finally {
      setLoadingBoutiques(false);
    }
  };

  const loadCategorieServices = async () => {
    try {
      const response = await adminService.getCategorieServices({ statut: true });
      setCategorieServices(response.data || []);
    } catch (err) {
      console.error('Error loading categorie services:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCategoryChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      categoryIds: newValue.map(cat => cat.id)
    }));
  };

  const handleUserChange = (event, newValue) => {
    setSelectedUser(newValue);
    setFormData(prev => ({
      ...prev,
      userId: newValue ? newValue.id : ''
    }));
  };

  const handleBoutiqueChange = (event, newValue) => {
    setSelectedBoutique(newValue);
    setFormData(prev => ({
      ...prev,
      boutiqueId: newValue ? newValue.id : ''
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    // Vérifier le nombre d'images (max 10 pour les services)
    if (formData.images.length + files.length > 10) {
      setError('Maximum 10 images autorisées pour un service');
      return;
    }

    // Vérifier les types de fichiers
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setError('Seules les images (JPEG, JPG, PNG, GIF, WEBP) sont autorisées');
      return;
    }

    // Vérifier la taille (50MB max par fichier - sera compressé en WebP)
    const oversizedFiles = files.filter(file => file.size > 50 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Les fichiers ne doivent pas dépasser 50MB');
      return;
    }

    setError('');
    setImageFiles(prev => [...prev, ...files]);

    // Créer des aperçus
    const newPreviews = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        newPreviews.push(event.target.result);
        if (newPreviews.length === files.length) {
          setImagePreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    // Uploader les fichiers
    setUploadingImages(true);
    try {
      // Utiliser la méthode spécifique pour les services (limite de 10 images)
      const response = await adminService.uploadServiceImages(files);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...response.imageUrls]
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'upload des images');
      // Retirer les fichiers ajoutés en cas d'erreur
      setImageFiles(prev => prev.slice(0, -files.length));
      setImagePreviews(prev => prev.slice(0, -files.length));
    } finally {
      setUploadingImages(false);
    }

    // Reset the input
    e.target.value = '';
  };

  const removeImage = async (index) => {
    // Supprimer l'image du serveur si elle a été uploadée
    const imageUrl = formData.images[index];
    if (imageUrl && imageUrl.includes('/imageProduit/')) {
      try {
        const filename = imageUrl.split('/').pop();
        await adminService.deleteProductImage(filename);
      } catch (err) {
        console.error('Erreur lors de la suppression de l\'image:', err);
      }
    }

    // Supprimer de tous les états
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.name.trim()) {
        throw new Error('Le nom du service est obligatoire');
      }

      if (!formData.userId && !formData.boutiqueId) {
        throw new Error('Le service doit être lié à un utilisateur ou à une boutique');
      }

      const submitData = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
        categoryIds: formData.categoryIds || []
      };

      if (service) {
        await adminService.updateService(service.id, submitData);
      } else {
        await adminService.createService(submitData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const selectedCategories = categorieServices.filter(cat => 
    formData.categoryIds.includes(cat.id)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {service ? 'Modifier le service' : 'Ajouter un service'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Nom du service"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            margin="normal"
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={3}
          />

          <TextField
            fullWidth
            label="Prix (FCFA)"
            name="price"
            type="number"
            value={formData.price}
            onChange={handleChange}
            margin="normal"
            inputProps={{ min: 0, step: 0.01 }}
          />

          <Autocomplete
            options={users}
            value={selectedUser}
            onChange={handleUserChange}
            getOptionLabel={(option) =>
              option ? `${option.name} (${option.email || option.phone || 'N/A'})` : ""
            }
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={loadingUsers}
            disabled={loadingUsers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Utilisateur"
                margin="normal"
                placeholder="Rechercher un utilisateur..."
                helperText="Tapez pour rechercher un utilisateur"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography variant="body1" fontWeight="medium">
                  {option.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.email || option.phone || 'N/A'}
                </Typography>
              </Box>
            )}
            noOptionsText={
              loadingUsers
                ? "Chargement des utilisateurs..."
                : "Aucun utilisateur trouvé"
            }
          />

          <Autocomplete
            options={boutiques}
            value={selectedBoutique}
            onChange={handleBoutiqueChange}
            getOptionLabel={(option) =>
              option ? option.name : ""
            }
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={loadingBoutiques}
            disabled={loadingBoutiques}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Boutique"
                margin="normal"
                placeholder="Rechercher une boutique..."
                helperText="Tapez pour rechercher une boutique"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography variant="body1" fontWeight="medium">
                  {option.name}
                </Typography>
                {option.address && (
                  <Typography variant="body2" color="text.secondary">
                    {option.address}
                  </Typography>
                )}
              </Box>
            )}
            noOptionsText={
              loadingBoutiques
                ? "Chargement des boutiques..."
                : "Aucune boutique trouvée"
            }
          />

          <Autocomplete
            multiple
            options={categorieServices}
            getOptionLabel={(option) => option.libelle}
            value={selectedCategories}
            onChange={handleCategoryChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Catégories"
                margin="normal"
                helperText="Sélectionnez une ou plusieurs catégories"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.libelle}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
          />

          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Images du service
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="service-images"
                multiple
                type="file"
                onChange={handleImageUpload}
                disabled={uploadingImages || formData.images.length >= 10}
              />
              <label htmlFor="service-images">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Add />}
                  fullWidth
                  disabled={uploadingImages || formData.images.length >= 10}
                >
                  {uploadingImages ? 'Upload en cours...' : 'Ajouter des images'}
                </Button>
              </label>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(formData.images.length > 0 ? formData.images : imagePreviews).map((image, index) => (
                <Card key={index} sx={{ width: 100, height: 100, position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="100"
                    image={image}
                    alt={`Service ${index + 1}`}
                    sx={{ objectFit: 'cover' }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeImage(index)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' }
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Card>
              ))}
            </Box>

            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              {formData.images.length}/10 images • Formats acceptés: JPEG, JPG, PNG, GIF, WEBP (max 50MB - sera compressé en WebP)
            </Typography>

            {uploadingImages && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="primary">
                  Upload en cours...
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isAvailable}
                  onChange={handleChange}
                  name="isAvailable"
                />
              }
              label="Disponible"
            />
          </Box>

          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleChange}
                  name="isActive"
                />
              }
              label="Actif"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Enregistrement...' : service ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ServiceModal;

